import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Row, Text, Badge, Panel, List, ListItem, Money, Tag, Button, Input, EmptyState } from '../ui';
import { api } from '../api/client';

type Filter = 'active' | 'archived' | 'deleted' | 'all';

export function DealsAll() {
  const [include, setInclude] = useState<Filter>('all');
  const [deals, setDeals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sectorFilter, setSectorFilter] = useState<string>('');

  useEffect(() => {
    setLoading(true);
    api.dealsAll(include).then(r => { setDeals(r.deals ?? []); setLoading(false); }).catch(() => setLoading(false));
  }, [include]);

  const sectors = Array.from(new Set(deals.map((d: any) => d.sector).filter(Boolean))).sort();
  const visible = deals.filter((d: any) =>
    (q === '' || d.name?.toLowerCase().includes(q.toLowerCase())) &&
    (sectorFilter === '' || d.sector === sectorFilter)
  );

  const stateOf = (d: any): { label: string; tone: 'positive'|'neutral'|'warning'|'negative'|'accent' } => {
    if (d.deletedAt) return { label: 'deleted', tone: 'negative' };
    if (d.archivedAt) return { label: 'archived', tone: 'warning' };
    if (d.dealStatus === 'completed') return { label: 'completed', tone: 'positive' };
    if (d.dealStatus === 'cancelled') return { label: 'cancelled', tone: 'neutral' };
    if (d.dealStatus === 'contract_signed') return { label: 'contract signed', tone: 'positive' };
    if (d.dealStatus === 'delivering') return { label: 'delivering', tone: 'positive' };
    return { label: d.stage ?? 'active', tone: 'accent' };
  };

  return (
    <Stack gap={3}>
      <Row justify="between" align="center">
        <Stack gap={0.5}>
          <Text variant="display">All deals</Text>
          <Text muted variant="small">archive view across active, archived, deleted, and completed</Text>
        </Stack>
        <Link to="/deals/new"><Button variant="primary" size="sm">+ new deal</Button></Link>
      </Row>

      <Row gap={2} wrap>
        <Row gap={0.5} className="overflow-x-auto no-scrollbar">
          {(['all','active','archived','deleted'] as Filter[]).map(f => (
            <button key={f} onClick={() => setInclude(f)}
              className={`shrink-0 snap-start h-9 sm:h-7 px-3 sm:px-2 hairline rounded-sm text-xs transition-colors ${include === f ? 'bg-ink text-bg' : 'text-muted hover:text-ink'}`}>
              {f}
            </button>
          ))}
        </Row>
        <div className="w-full sm:w-72"><Input value={q} onChange={e => setQ(e.target.value)} placeholder="search by name…" /></div>
        {sectors.length > 1 && (
          <select value={sectorFilter} onChange={e => setSectorFilter(e.target.value)}
            className="h-9 sm:h-9 hairline rounded-sm px-2 text-xs sm:text-sm bg-bg">
            <option value="">all sectors</option>
            {sectors.map(s => <option key={s as string} value={s as string}>{s as string}</option>)}
          </select>
        )}
      </Row>

      <Panel title={`${visible.length} deal${visible.length === 1 ? '' : 's'}`}>
        {loading ? <Text variant="small" muted>loading…</Text> :
          visible.length === 0 ?
            <EmptyState title="no deals" hint="create your first deal or change the filter" action={<Link to="/deals/new"><Button variant="primary" size="sm">+ new deal</Button></Link>} /> :
            <List>
              {visible.map((d: any) => {
                const state = stateOf(d);
                return (
                  <ListItem key={d.id}>
                    <Link to={`/deals/${d.id}`} className="block">
                      <Row justify="between">
                        <Stack gap={0.5}>
                          <Row gap={1} wrap>
                            <Text variant="small" className="font-medium">{d.name}</Text>
                            <Badge tone={state.tone}>{state.label}</Badge>
                          </Row>
                          <Row gap={2} wrap>
                            {d.sector && <Text variant="label">{d.sector}</Text>}
                            {d.geography && <Text variant="label">{d.geography}</Text>}
                          </Row>
                        </Stack>
                        {d.ticketSize > 0 && <Money amount={d.ticketSize} currency={d.currency || 'USD'} className="text-sm" />}
                      </Row>
                    </Link>
                  </ListItem>
                );
              })}
            </List>
        }
      </Panel>
    </Stack>
  );
}
